import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { appointmentService } from '../services/AppointmentService';
import { clientService } from '../services/ClientService';
import { userService } from '../services/UserService';
import { Appointment, AppointmentWithNames } from '../models/Appointment';
import { useBeginSession } from '../hooks/useBeginSession';
import { Calendar, Clock, MapPin, Upload, FileText, Video } from 'lucide-react';

export const RBTDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [todaysSessions, setTodaysSessions] = useState<AppointmentWithNames[]>([]);
  const [tomorrowsSessions, setTomorrowsSessions] = useState<AppointmentWithNames[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadSessions = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Get all appointments for this RBT
      const appointments = await appointmentService.getAppointmentsByRbt(user.id);

      // Set up date ranges
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dayAfterTomorrow = new Date(tomorrow);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);

      // Filter for today's and tomorrow's appointments (completed/cancelled
      // ones are no longer actionable, so they drop off).
      const isActive = (appt: Appointment) =>
        appt.status !== 'completed' && appt.status !== 'cancelled';

      const todaysAppts = appointments.filter((appt: Appointment) => {
        const apptDate = new Date(appt.startTime);
        return apptDate >= today && apptDate < tomorrow && isActive(appt);
      });

      const tomorrowsAppts = appointments.filter((appt: Appointment) => {
        const apptDate = new Date(appt.startTime);
        return apptDate >= tomorrow && apptDate < dayAfterTomorrow && isActive(appt);
      });

      // Enrich today's appointments
      const enrichedTodaysAppts: AppointmentWithNames[] = await Promise.all(
        todaysAppts.map(async (appt) => {
          const [client, supervisor] = await Promise.all([
            clientService.getClient(appt.clientId),
            userService.getUser(appt.supervisorId),
          ]);

          return {
            ...appt,
            clientName: client ? `${client.firstName} ${client.lastName}` : 'Unknown Client',
            rbtName: user.name,
            supervisorName: supervisor?.name || 'Unknown Supervisor',
          };
        })
      );

      // Enrich tomorrow's appointments
      const enrichedTomorrowsAppts: AppointmentWithNames[] = await Promise.all(
        tomorrowsAppts.map(async (appt) => {
          const [client, supervisor] = await Promise.all([
            clientService.getClient(appt.clientId),
            userService.getUser(appt.supervisorId),
          ]);

          return {
            ...appt,
            clientName: client ? `${client.firstName} ${client.lastName}` : 'Unknown Client',
            rbtName: user.name,
            supervisorName: supervisor?.name || 'Unknown Supervisor',
          };
        })
      );

      // Sort by start time
      enrichedTodaysAppts.sort((a, b) => {
        const timeA = new Date(a.startTime).getTime();
        const timeB = new Date(b.startTime).getTime();
        return timeA - timeB;
      });

      enrichedTomorrowsAppts.sort((a, b) => {
        const timeA = new Date(a.startTime).getTime();
        const timeB = new Date(b.startTime).getTime();
        return timeA - timeB;
      });

      setTodaysSessions(enrichedTodaysAppts);
      setTomorrowsSessions(enrichedTomorrowsAppts);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Start Session — the shared entry-point action (Phase 3), same as My Sessions.
  const beginSession = useBeginSession();

  return (
    <div className="rbt-dashboard">
      {/* Welcome Section */}
      <div className="rbt-welcome">
        <h1>Welcome back, {user?.name}!</h1>
        <p className="rbt-email">
          {user?.email}
          <span className={`role-badge role-${user?.role}`} style={{ marginLeft: '8px' }}>
            {user?.role?.toUpperCase()}
          </span>
        </p>
      </div>

      {/* Quick Actions */}
      <div className="rbt-quick-actions">
        <button
          className="rbt-action-btn rbt-action-primary"
          onClick={() => navigate('/appointments')}
        >
          <Calendar size={24} />
          <span>Schedule</span>
        </button>

        <button
          className="rbt-action-btn rbt-action-primary"
          onClick={() => navigate('/footage')}
        >
          <Video size={24} />
          <span>Stream</span>
        </button>

        <button
          className="rbt-action-btn rbt-action-primary"
          onClick={() => navigate('/session-notes')}
        >
          <FileText size={24} />
          <span>Report</span>
        </button>

        <button
          className="rbt-action-btn rbt-action-secondary"
          onClick={() => navigate('/upload')}
        >
          <Upload size={24} />
          <span>Upload</span>
        </button>
      </div>

      {loading ? (
        <div className="rbt-loading">Loading sessions...</div>
      ) : error ? (
        <div className="rbt-error">{error}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-6)' }}>
          {/* Today's Sessions */}
          <div className="rbt-sessions-section">
            <div className="rbt-section-header">
              <Calendar size={24} />
              <h2>Today's Sessions</h2>
            </div>

            {todaysSessions.length === 0 ? (
              <div className="rbt-empty-state">
                <Calendar size={48} />
                <p>No sessions scheduled for today</p>
              </div>
            ) : (
              <div className="rbt-sessions-list">
                {todaysSessions.map((session) => (
                  <div key={session.id} className="rbt-session-card">
                    <div className="rbt-session-header">
                      <div className="rbt-session-time">
                        <Clock size={18} />
                        <span>
                          {formatTime(session.startTime)} - {formatTime(session.endTime)}
                        </span>
                      </div>
                      <span className={`rbt-session-status status-${session.status}`}>
                        {session.status}
                      </span>
                    </div>

                    <div className="rbt-session-client">
                      <h3>{session.clientName}</h3>
                    </div>

                    <div className="rbt-session-location">
                      <MapPin size={16} />
                      <span>
                        {session.location.address}, {session.location.city}, {session.location.state}
                      </span>
                    </div>

                    {session.notes && (
                      <div className="rbt-session-notes">
                        <p>{session.notes}</p>
                      </div>
                    )}

                    <button
                      className="rbt-start-session-btn"
                      onClick={() =>
                        beginSession(session, {
                          clientName: session.clientName,
                          supervisorName: session.supervisorName,
                        })
                      }
                    >
                      Start Session
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tomorrow's Sessions */}
          <div className="rbt-sessions-section">
            <div className="rbt-section-header">
              <Calendar size={24} />
              <h2>Tomorrow's Sessions</h2>
            </div>

            {tomorrowsSessions.length === 0 ? (
              <div className="rbt-empty-state">
                <Calendar size={48} />
                <p>No sessions scheduled for tomorrow</p>
              </div>
            ) : (
              <div className="rbt-sessions-list">
                {tomorrowsSessions.map((session) => (
                  <div key={session.id} className="rbt-session-card">
                    <div className="rbt-session-header">
                      <div className="rbt-session-time">
                        <Clock size={18} />
                        <span>
                          {formatTime(session.startTime)} - {formatTime(session.endTime)}
                        </span>
                      </div>
                      <span className={`rbt-session-status status-${session.status}`}>
                        {session.status}
                      </span>
                    </div>

                    <div className="rbt-session-client">
                      <h3>{session.clientName}</h3>
                    </div>

                    <div className="rbt-session-location">
                      <MapPin size={16} />
                      <span>
                        {session.location.address}, {session.location.city}, {session.location.state}
                      </span>
                    </div>

                    {session.notes && (
                      <div className="rbt-session-notes">
                        <p>{session.notes}</p>
                      </div>
                    )}

                    <button
                      className="rbt-start-session-btn"
                      onClick={() => navigate('/appointments')}
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
